import React, {forwardRef, useRef} from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import {
  ScrollAreaProps,
  ScrollAreaViewportProps,
  ScrollAreaScrollbarProps,
  ScrollAreaThumbProps,
  ScrollAreaCornerProps
} from './types'

// ------------------------------------------------------
// Root
/** Contains all the parts of a scroll area. */
export const Root = forwardRef(({children, sx: sxProp, ...props}: ScrollAreaProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)

  return (
    <ScrollAreaPrimitive.Root ref={innerRef} {...props}>
      {children}
    </ScrollAreaPrimitive.Root>
  )
})

// ------------------------------------------------------
// Viewport
/** The viewport area of the scroll area.  */
export const Viewport = forwardRef(({children, ...props}: ScrollAreaViewportProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ScrollAreaPrimitive.Viewport ref={innerRef} {...props}>
      {children}
    </ScrollAreaPrimitive.Viewport>
  )
})

// ------------------------------------------------------
// Scrollbar
/** The vertical scrollbar. Add a second Scrollbar with an orientation prop to enable horizontal scrolling. */
export const Scrollbar = forwardRef(({children, testId, ...props}: ScrollAreaScrollbarProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ScrollAreaPrimitive.Scrollbar ref={innerRef} data-testid={testId} {...props}>
      {children}
    </ScrollAreaPrimitive.Scrollbar>
  )
})
// ------------------------------------------------------
// Thumb
/** The thumb to be used in ScrollArea.Scrollbar. */
export const Thumb = forwardRef(({children, ...props}: ScrollAreaThumbProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ScrollAreaPrimitive.Thumb ref={innerRef} {...props}>
      {children}
    </ScrollAreaPrimitive.Thumb>
  )
})
// ------------------------------------------------------
// Corner
/** The corner where both vertical and horizontal scrollbars meet. */
export const Corner = forwardRef(({children, ...props}: ScrollAreaCornerProps, forwardedRef) => {
  const innerRef = useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <ScrollAreaPrimitive.Corner ref={innerRef} {...props}>
      {children}
    </ScrollAreaPrimitive.Corner>
  )
})

// Exports
Root.displayName = 'ScrollArea'
Viewport.displayName = 'Viewport'
Scrollbar.displayName = 'Scrollbar'
Thumb.displayName = 'Thumb'
Corner.displayName = 'Corner'

export default Object.assign(Root, {Viewport, Scrollbar, Thumb, Corner})
